/**
 * Cache Manager
 * 
 * Provides multi-layer caching:
 * 1. Memory cache (fast, session-only)
 * 2. LocalStorage cache (persistent, limited size)
 * 3. API response caching with TTL
 */

// In-memory cache (fastest, but cleared on page refresh)
const memoryCache = new Map();

// Cache configuration
const CACHE_CONFIG = {
  // Time To Live (in milliseconds)
  TTL: {
    SHORT: 1 * 60 * 1000,      // 1 minute
    MEDIUM: 5 * 60 * 1000,     // 5 minutes
    LONG: 15 * 60 * 1000,      // 15 minutes
    HOUR: 60 * 60 * 1000,      // 1 hour
    DAY: 24 * 60 * 60 * 1000,  // 24 hours
  },
  
  // Cache keys for different data types
  KEYS: {
    USER_PROFILE: 'user_profile',
    ORDERS: 'orders_',
    PRICING: 'pricing_config',
    COMPANY_PROFILE: 'company_profile',
    DESIGNS: 'designs_gallery',
    TEMPLATES: 'pdf_templates',
  },
  
  // Max localStorage size (5MB limit, use 4MB to be safe)
  MAX_STORAGE_SIZE: 4 * 1024 * 1024,
};

/**
 * Check if cache entry is expired
 */
const isExpired = (timestamp, ttl) => {
  if (!timestamp) return true;
  return Date.now() - timestamp > ttl;
};

/**
 * Get from memory cache
 */
export const getFromMemory = (key) => {
  const cached = memoryCache.get(key);
  if (!cached) return null;
  
  if (isExpired(cached.timestamp, cached.ttl)) {
    memoryCache.delete(key);
    return null;
  }
  
  return cached.data;
};

/**
 * Set in memory cache
 */
export const setInMemory = (key, data, ttl = CACHE_CONFIG.TTL.MEDIUM) => {
  memoryCache.set(key, {
    data,
    timestamp: Date.now(),
    ttl,
  });
};

/**
 * Get from localStorage cache
 */
export const getFromStorage = (key) => {
  try {
    const cached = localStorage.getItem(`cache_${key}`);
    if (!cached) return null;
    
    const parsed = JSON.parse(cached);
    
    if (isExpired(parsed.timestamp, parsed.ttl)) {
      localStorage.removeItem(`cache_${key}`);
      return null;
    }
    
    return parsed.data;
  } catch (error) {
    console.warn('Cache read error:', error);
    return null;
  }
};

/**
 * Set in localStorage cache
 */
export const setInStorage = (key, data, ttl = CACHE_CONFIG.TTL.MEDIUM) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      ttl,
    };
    
    const serialized = JSON.stringify(cacheEntry);
    
    // Check if it fits in localStorage
    if (serialized.length > CACHE_CONFIG.MAX_STORAGE_SIZE) {
      console.warn('Data too large for localStorage cache');
      return false;
    }
    
    localStorage.setItem(`cache_${key}`, serialized);
    return true;
  } catch (error) {
    // localStorage might be full
    if (error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old cache');
      clearOldCache();
      // Try again
      try {
        localStorage.setItem(`cache_${key}`, JSON.stringify({
          data,
          timestamp: Date.now(),
          ttl,
        }));
        return true;
      } catch (e) {
        console.error('Failed to cache after cleanup:', e);
        return false;
      }
    }
    console.error('Cache write error:', error);
    return false;
  }
};

/**
 * Get from cache (tries memory first, then localStorage)
 */
export const getCache = (key) => {
  // Try memory cache first (fastest)
  let data = getFromMemory(key);
  if (data !== null) {
    return data;
  }
  
  // Fall back to localStorage
  data = getFromStorage(key);
  if (data !== null) {
    // Promote to memory cache
    setInMemory(key, data);
    return data;
  }
  
  return null;
};

/**
 * Set cache (stores in both memory and localStorage)
 */
export const setCache = (key, data, ttl = CACHE_CONFIG.TTL.MEDIUM) => {
  setInMemory(key, data, ttl);
  setInStorage(key, data, ttl);
};

/**
 * Invalidate cache entry
 */
export const invalidateCache = (key) => {
  memoryCache.delete(key);
  localStorage.removeItem(`cache_${key}`);
};

/**
 * Invalidate cache by pattern
 */
export const invalidateCachePattern = (pattern) => {
  // Clear from memory cache
  for (const key of memoryCache.keys()) {
    if (key.includes(pattern)) {
      memoryCache.delete(key);
    }
  }
  
  // Clear from localStorage
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cache_') && key.includes(pattern)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

/**
 * Clear old cache entries to free up space
 */
const clearOldCache = () => {
  const keysToRemove = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cache_')) {
      try {
        const cached = JSON.parse(localStorage.getItem(key));
        if (isExpired(cached.timestamp, cached.ttl)) {
          keysToRemove.push(key);
        }
      } catch (e) {
        // Invalid JSON, remove it
        keysToRemove.push(key);
      }
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  console.debug(`Cleared ${keysToRemove.length} old cache entries`);
};

/**
 * Clear all cache
 */
export const clearAllCache = () => {
  memoryCache.clear();
  
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cache_')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  console.debug('All cache cleared');
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  let storageSize = 0;
  let storageEntries = 0;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cache_')) {
      storageEntries++;
      storageSize += localStorage.getItem(key).length;
    }
  }
  
  return {
    memory: {
      entries: memoryCache.size,
    },
    storage: {
      entries: storageEntries,
      sizeKB: (storageSize / 1024).toFixed(2),
      sizeMB: (storageSize / (1024 * 1024)).toFixed(2),
    },
  };
};

/**
 * Cached API call wrapper
 */
export const cachedApiCall = async (
  key,
  apiFunction,
  ttl = CACHE_CONFIG.TTL.MEDIUM,
  forceRefresh = false
) => {
  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = getCache(key);
    if (cached !== null) {
      return cached;
    }
  }
  
  // Make API call
  try {
    const result = await apiFunction();
    
    // Cache the result
    setCache(key, result, ttl);
    
    return result;
  } catch (error) {
    // If API fails, try to return stale cache
    const staleCache = getFromStorage(key);
    if (staleCache !== null) {
      console.warn('API failed, returning stale cache');
      return staleCache;
    }
    
    throw error;
  }
};

/**
 * Broadcast cache invalidation to other tabs/sessions
 */
export const broadcastCacheInvalidation = (pattern) => {
  try {
    localStorage.setItem('cache_invalidation', JSON.stringify({
      pattern,
      timestamp: Date.now()
    }));
    // Remove after a short delay to avoid accumulation
    setTimeout(() => {
      localStorage.removeItem('cache_invalidation');
    }, 1000);
  } catch (error) {
    console.warn('[Cache] Failed to broadcast invalidation:', error);
  }
};

/**
 * Listen for cache invalidation events from other tabs
 */
const initializeCacheInvalidation = () => {
  window.addEventListener('storage', (event) => {
    if (event.key === 'cache_invalidation' && event.newValue) {
      try {
        const { pattern, timestamp } = JSON.parse(event.newValue);
        if (Date.now() - timestamp < 5000) { // Only process recent invalidations
          invalidateCachePattern(pattern);
          console.debug('[Cache] Received invalidation broadcast:', pattern);
        }
      } catch (error) {
        console.warn('[Cache] Failed to process invalidation broadcast:', error);
      }
    }
  });
};

// Export cache config for use in other modules
export { CACHE_CONFIG };

// Initialize cache invalidation system
if (typeof window !== 'undefined') {
  initializeCacheInvalidation();
}

// Cleanup old cache entries on app start (safe cleanup)
if (typeof window !== 'undefined') {
  // Run cleanup after app is fully loaded, with longer delay
  window.addEventListener('load', () => {
    setTimeout(() => {
      try {
        clearOldCache();
      } catch (error) {
        console.warn('[CacheManager] Cleanup error:', error);
      }
    }, 10000); // 10 second delay to ensure app is stable
  });
}
